import React from 'react';
import GridCountCell from './grid/GridCountCell';

// =============================================================================
// SchedulePanel
// =============================================================================
// The LC dashboard's "Delivery Schedule" panel: a two-week (current + following,
// Mon–Sun) grid of move counts by the day each order is scheduled for delivery.
// Purely presentational — it renders the counts the backend returns and reports
// cell clicks via onSelectCell. Each week is a band of four rows: the bold week
// total (all scheduled that day), then the Not Dispatched, No Outgate and Not
// Delivered subsets (Not Delivered = delivery appt that day, delivery leg not arrived).
//
// Lives inside a <DashboardTile> (which supplies the title + drag handle), so it
// renders bare. Pairs with the /lc-dashboards/schedule-counts endpoint.

export type ScheduleCategory = 'total' | 'not_dispatched' | 'no_outgate' | 'not_delivered';

export interface ScheduleDay {
  total: number;
  not_dispatched: number;
  no_outgate: number;
  not_delivered: number;
}

const EMPTY_DAY: ScheduleDay = { total: 0, not_dispatched: 0, no_outgate: 0, not_delivered: 0 };

export interface ScheduleData {
  week_starts: string[];                  // two ISO Monday dates (current + following week)
  today: string;                          // server's today (ISO) for urgency coloring
  days: { [iso: string]: ScheduleDay };   // counts keyed by ISO date (missing = 0)
  // Per-week DISTINCT-PRO totals keyed by the week's Monday (the Total column). These
  // are a fresh count over the whole week, NOT the sum of the days — an order scheduled
  // on two days in the same week is counted once, matching its Total-cell drill-down.
  weeks?: { [iso: string]: ScheduleDay };
}

export interface SchedulePanelProps {
  data: ScheduleData | null;
  loading: boolean;
  // Fired when a non-zero count is clicked. `start`/`end` bound the day(s) the cell
  // covers — equal for a single day, a Mon–Sun span for the week Total column.
  onSelectCell?: (category: ScheduleCategory, start: string, end: string) => void;
}

const DOW = ['M', 'Tu', 'W', 'Th', 'F', 'S', 'Su'];

// ISO date math without timezone drift (treat as a plain calendar date).
function addDays(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}
function mmdd(iso: string): string {
  const parts = iso.split('-');
  return `${parts[1]}/${parts[2]}`;
}

const SUB_ROWS: { key: Exclude<ScheduleCategory, 'total'>; label: string }[] = [
  { key: 'not_dispatched', label: 'Not Dispatched' },
  { key: 'no_outgate', label: 'No Outgate' },
  { key: 'not_delivered', label: 'Not Delivered' },
];

const SchedulePanel: React.FC<SchedulePanelProps> = ({ data, loading, onSelectCell }) => {
  // Urgency color for a subset cell: red once the scheduled day is past, orange on
  // the day itself. Neutral when there's nothing to flag (count 0 or future).
  const cellColor = (date: string, value: number): string => {
    if (!data || value <= 0) return '';
    if (date < data.today) return 'bg-red-600 text-white';
    if (date === data.today) return 'bg-orange-500 text-white';
    return '';
  };

  const renderCount = (
    category: ScheduleCategory,
    start: string,
    end: string,
    value: number,
    opts: { bold?: boolean; colored?: boolean } = {}
  ) => {
    // Color uses the cell's own day; the week Total column (start !== end) isn't colored.
    const colorClass = opts.colored && start === end ? cellColor(start, value) : '';
    return (
      <GridCountCell
        value={value}
        bold={opts.bold}
        colorClass={colorClass}
        placeholder={value === 0 && loading && !data}
        onSelect={onSelectCell ? () => onSelectCell(category, start, end) : undefined}
        title={start === end ? `View orders for ${start}` : `View orders for ${start} – ${end}`}
      />
    );
  };

  const weekStarts = data?.week_starts ?? [];

  return (
    // w-full so the table fills the tile width (header + cells stretch to the panel
    // edges); natural height keeps the tile's double-click fit-to-content working.
    <div className="w-full bg-panel text-sm">
      <table className="border-collapse w-full">
        <thead>
          <tr className="bg-slate-700 text-white">
            <th className="text-left font-semibold px-2 py-1 min-w-[120px]">Delivery Schedule</th>
            {DOW.map((d) => (
              <th key={d} className="text-right font-semibold px-2 py-1 w-[52px]">{d}</th>
            ))}
            <th className="text-right font-semibold px-2 py-1 w-[60px]">Total</th>
          </tr>
        </thead>
        <tbody className="text-body">
          {weekStarts.map((weekStart) => {
            const dates = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
            // Missing day = no orders; a day from an older payload that predates a
            // subset key reads 0 for it rather than rendering `undefined`.
            const dayOf = (iso: string): ScheduleDay => ({ ...EMPTY_DAY, ...(data?.days[iso] ?? {}) });
            // Week Total uses the backend's DISTINCT-PRO week count (not a sum of days),
            // so an order on two days in the week is counted once — matching the Total
            // cell's drill-down. Falls back to the day-sum only if `weeks` is absent.
            const weekOf = data?.weeks?.[weekStart];
            const weekTotal = (cat: ScheduleCategory) =>
              weekOf ? (weekOf[cat] ?? 0) : dates.reduce((acc, iso) => acc + dayOf(iso)[cat], 0);
            const weekEnd = addDays(weekStart, 6);

            return (
              <React.Fragment key={weekStart}>
                {/* Week band: bold per-day totals + week total */}
                <tr className="border-t border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30">
                  <td className="font-bold px-2 py-1 whitespace-nowrap">
                    {mmdd(weekStart)} - {mmdd(weekEnd)}
                  </td>
                  {dates.map((iso) => (
                    <td key={iso} className="border-l border-gray-100 dark:border-gray-700/50">
                      {renderCount('total', iso, iso, dayOf(iso).total, { bold: true })}
                    </td>
                  ))}
                  <td className="border-l border-gray-300 dark:border-gray-600">
                    {renderCount('total', weekStart, weekEnd, weekTotal('total'), { bold: true })}
                  </td>
                </tr>
                {/* Subset rows */}
                {SUB_ROWS.map((row) => (
                  <tr key={row.key} className="border-t border-gray-100 dark:border-gray-700/50">
                    <td className="px-2 py-1 whitespace-nowrap pl-4 text-label">{row.label}</td>
                    {dates.map((iso) => (
                      <td key={iso} className="border-l border-gray-100 dark:border-gray-700/50">
                        {renderCount(row.key, iso, iso, dayOf(iso)[row.key], { colored: true })}
                      </td>
                    ))}
                    <td className="border-l border-gray-300 dark:border-gray-600">
                      {renderCount(row.key, weekStart, weekEnd, weekTotal(row.key))}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
            );
          })}
          {weekStarts.length === 0 && (
            <tr>
              <td colSpan={DOW.length + 2} className="text-center text-gray-400 dark:text-gray-500 px-2 py-4">
                {loading ? 'Loading…' : 'No schedule data.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SchedulePanel;
